/**
 * BookingDetailContent — 예약 상세 공통 UI 콘텐츠
 *
 * Sheet와 Page 양쪽에서 동일하게 사용되는 내부 콘텐츠.
 * 래퍼(Sheet/Page)가 컨테이너를 제공하고, 이 컴포넌트는 내부만 렌더링한다.
 */

import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import ClickablePhoneNumber from "@/components/common/ClickablePhoneNumber";
import ClickableCustomerName from "@/components/common/ClickableCustomerName";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { UnifiedMemoPanel } from "@/components/memo/UnifiedMemoPanel";
import { RecordingPlayer } from "@/components/common/RecordingPlayer";
import type { AppointmentTimeline } from "@/types/appointment";
import dayjs from "dayjs";
import { formatDateShortWithDay } from "@/utils/dateFormatter";
import {
  AlertTriangle,
  User,
  Phone,
  Calendar,
  Clock,
  Stethoscope,
  MessageSquare,
  X,
  Check,
  Ban,
  UserMinus,
  Edit,
  CheckCircle2,
  PhoneCall,
  Tag,
  Shield,
  FileText,
  Activity,
  History,
  Loader2,
  Users,
} from "lucide-react";
import { cn } from "@/lib/utils";
import RescheduleDialog from "./RescheduleDialog";
import { AppointmentStatusBadge } from "@/components/common/StatusBadge";
import { useBookingDetail } from "@/hooks/useBookingDetail";
import {
  getSourceLabel,
  getSourceIcon,
  formatDuration,
  formatPrice,
  getGenderLabel,
  getAgeFromBirthDate,
  getStatusColor,
  getHistoryIcon,
} from "@/utils/appointmentHelpers";

export interface BookingDetailContentProps {
  appointment: AppointmentTimeline;
  onDataChange?: () => void;
  /** Sheet: onOpenChange(false), Page: navigate(-1) */
  onClose?: () => void;
  containerType: "sheet" | "page";
}

export default function BookingDetailContent({
  appointment,
  onDataChange,
  onClose,
  containerType,
}: BookingDetailContentProps) {
  const {
    // Queries
    statusHistory,
    isHistoryLoading,
    stats,
    appointmentHistory,
    isAppointmentHistoryLoading,
    customerConsults,
    clinicSettings,
    staffList,
    matchedProduct,
    otherPendingAppointments,
    // Mutations
    approveMutation,
    rejectMutation,
    cancelMutation,
    noShowMutation,
    completeMutation,
    assignStaffMutation,
    toggleBlockMutation,
    // UI state
    rejectReason,
    setRejectReason,
    isRejecting,
    setIsRejecting,
    cancelReason,
    setCancelReason,
    isCancelling,
    setIsCancelling,
    isRescheduleOpen,
    setIsRescheduleOpen,
    selectedStaffId,
    setSelectedStaffId,
    // Handlers
    handleApprove,
    handleReject,
    handleNoShow,
    handleComplete,
    handleCancel,
    invalidateAll,
  } = useBookingDetail({
    appointment,
    enabled: true,
    onDataChange,
  });

  const hasBothChoices =
    appointment.firstChoiceDate && appointment.secondChoiceDate;

  const genderLabel = getGenderLabel(appointment.customerGender);
  const age = getAgeFromBirthDate(appointment.customerBirthDate);
  const customerDesc = [genderLabel, age ? `${age}세` : null]
    .filter(Boolean)
    .join("/");

  const hasCallback =
    appointment.callbackStatus && appointment.callbackStatus !== "NOT_NEEDED";
  const hasNotes = appointment.servicePreNotes || appointment.servicePostNotes;

  const defaultOpenSections = useMemo(() => {
    const sections = ["info", "memo"];
    if (appointment.source === "CALL") sections.push("source");
    return sections;
  }, [appointment.source]);

  const isPage = containerType === "page";

  // ===== Render =====

  return (
    <div className="flex flex-col h-full">
      {/* ===== HEADER ===== */}
      <header
        className={cn(
          "border-b bg-background",
          !isPage && cn("border-l-4", getStatusColor(appointment.status)),
        )}
      >
        {/* Row 1: Navigation + Status */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-3">
            <AppointmentStatusBadge status={appointment.status} />
            <h2 className="text-lg font-bold">예약 상세</h2>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="gap-1">
              {getSourceIcon(appointment.source)}
              {getSourceLabel(appointment.source)}
            </Badge>
            {appointment.priority === "URGENT" && (
              <Badge className="bg-destructive/10 text-destructive border-destructive/20">
                긴급
              </Badge>
            )}
          </div>
        </div>

        {/* Row 2: Customer Card */}
        <div className="mx-4 mb-2 p-3 rounded-lg border bg-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <ClickableCustomerName
                    customerId={appointment.customerId}
                    name={appointment.customerName}
                    className="font-semibold"
                  />
                  {customerDesc && (
                    <span className="text-sm text-muted-foreground">
                      ({customerDesc})
                    </span>
                  )}
                  {appointment.customerBookingBlocked && (
                    <Badge
                      variant="destructive"
                      className="text-[10px] h-5 px-1.5"
                    >
                      <Ban className="h-3 w-3 mr-0.5" />
                      차단
                    </Badge>
                  )}
                </div>
                {appointment.customerPhone && (
                  <ClickablePhoneNumber
                    phone={appointment.customerPhone}
                    className="text-sm text-muted-foreground"
                  />
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span>
                방문{" "}
                <strong className="text-success">{stats.visitCount}</strong>
              </span>
              <span>
                노쇼{" "}
                <strong className="text-destructive">{stats.noShowCount}</strong>
              </span>
              <span>
                취소{" "}
                <strong className="text-muted-foreground">{stats.cancelCount}</strong>
              </span>
            </div>
          </div>
        </div>

        {/* Row 3: At-a-Glance */}
        <div className="mx-4 mb-2 p-3 rounded-lg border bg-muted/30">
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <span>
                {appointment.firstChoiceDate
                  ? formatDateShortWithDay(appointment.firstChoiceDate)
                  : appointment.appointmentDate
                    ? formatDateShortWithDay(appointment.appointmentDate)
                    : "미정"}{" "}
                {appointment.firstChoiceTime || appointment.time}
                {appointment.serviceName && (
                  <>
                    {" · "}
                    {appointment.department}
                    {appointment.subItemName && `(${appointment.subItemName})`}
                  </>
                )}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Stethoscope className="h-3.5 w-3.5 text-muted-foreground" />
              <span>
                {formatPrice(
                  appointment.servicePriceMin,
                  appointment.servicePriceMax,
                  appointment.servicePriceDisplayEnabled,
                ) || "가격 미설정"}
                {appointment.serviceDurationMinutes &&
                  ` · ${appointment.serviceDurationMinutes}분`}
                {appointment.doctorName && ` · ${appointment.doctorName}`}
              </span>
            </div>
            {(appointment.consultSubTypeLabel || appointment.aiConfidence) && (
              <div className="flex items-center gap-2">
                <Activity className="h-3.5 w-3.5 text-muted-foreground" />
                <span>
                  {appointment.consultSubTypeLabel || "분석없음"}
                  {appointment.source === "CALL" && " · AI전화"}
                  {appointment.aiConfidence != null &&
                    ` · 신뢰도 ${Math.round(appointment.aiConfidence * 100)}%`}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Row 4: Duplicate Warning */}
        {otherPendingAppointments.length > 0 && (
          <div className="mx-4 mb-2 p-2 rounded-lg bg-amber-50 border border-amber-200">
            <div className="flex items-center gap-2 text-amber-700 text-xs">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                이 고객에게 <strong>{otherPendingAppointments.length}건</strong>
                의 다른 미처리 예약이 있습니다
              </span>
            </div>
          </div>
        )}
      </header>

      {/* ===== SCROLLABLE BODY ===== */}
      {isPage ? (
        <ScrollArea className="flex-1">
          <AccordionBody
            appointment={appointment}
            defaultOpenSections={defaultOpenSections}
            hasBothChoices={!!hasBothChoices}
            clinicSettings={clinicSettings}
            matchedProduct={matchedProduct}
            staffList={staffList}
            selectedStaffId={selectedStaffId}
            setSelectedStaffId={setSelectedStaffId}
            assignStaffMutation={assignStaffMutation}
            statusHistory={statusHistory}
            isHistoryLoading={isHistoryLoading}
            appointmentHistory={appointmentHistory}
            isAppointmentHistoryLoading={isAppointmentHistoryLoading}
            customerConsults={customerConsults}
            stats={stats}
            toggleBlockMutation={toggleBlockMutation}
            handleApprove={handleApprove}
            approveMutation={approveMutation}
            hasCallback={!!hasCallback}
            hasNotes={!!hasNotes}
          />
        </ScrollArea>
      ) : (
        <ScrollArea className="flex-1">
          <AccordionBody
            appointment={appointment}
            defaultOpenSections={defaultOpenSections}
            hasBothChoices={!!hasBothChoices}
            clinicSettings={clinicSettings}
            matchedProduct={matchedProduct}
            staffList={staffList}
            selectedStaffId={selectedStaffId}
            setSelectedStaffId={setSelectedStaffId}
            assignStaffMutation={assignStaffMutation}
            statusHistory={statusHistory}
            isHistoryLoading={isHistoryLoading}
            appointmentHistory={appointmentHistory}
            isAppointmentHistoryLoading={isAppointmentHistoryLoading}
            customerConsults={customerConsults}
            stats={stats}
            toggleBlockMutation={toggleBlockMutation}
            handleApprove={handleApprove}
            approveMutation={approveMutation}
            hasCallback={!!hasCallback}
            hasNotes={!!hasNotes}
          />
        </ScrollArea>
      )}

      {/* ===== FOOTER (sticky actions) ===== */}
      <footer className="border-t p-4 bg-background">
        {isRejecting ? (
          <div className="space-y-3">
            <Label className="text-destructive font-medium">거절 사유 (선택)</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="거절 사유를 입력하세요 (선택사항)"
              className="min-h-[60px]"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsRejecting(false)}
              >
                취소
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleReject}
                disabled={rejectMutation.isPending}
              >
                {rejectMutation.isPending ? "처리 중..." : "거절 확정"}
              </Button>
            </div>
          </div>
        ) : isCancelling ? (
          <div className="space-y-3">
            <Label className="text-destructive font-medium">취소 사유 (선택)</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="취소 사유를 입력하세요 (선택사항)"
              className="min-h-[60px]"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsCancelling(false)}
              >
                돌아가기
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleCancel}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? "처리 중..." : "취소 확정"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* 신청 상태: 승인/거절 */}
            {appointment.needsAction && (
              <div className="space-y-2">
                {hasBothChoices && (
                  <div className="flex gap-2">
                    <Button
                      className="flex-1 bg-info hover:bg-info/90"
                      onClick={() => handleApprove(1)}
                      disabled={approveMutation.isPending}
                    >
                      <Check className="h-4 w-4 mr-1" />
                      1순위 승인
                    </Button>
                    <Button
                      className="flex-1 bg-warning hover:bg-warning/90"
                      onClick={() => handleApprove(2)}
                      disabled={approveMutation.isPending}
                    >
                      <Check className="h-4 w-4 mr-1" />
                      2순위 승인
                    </Button>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={() => setIsRejecting(true)}
                  >
                    <X className="h-4 w-4 mr-1" />
                    거절
                  </Button>
                  {!hasBothChoices && (
                    <Button
                      className="flex-1 bg-success hover:bg-success/90"
                      onClick={() => handleApprove()}
                      disabled={approveMutation.isPending}
                    >
                      <Check className="h-4 w-4 mr-1" />
                      {approveMutation.isPending ? "처리 중..." : "승인"}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Naver Place 4-Button */}
            {(appointment.status === "CONFIRMED" ||
              appointment.status === "REQUESTED" ||
              appointment.status === "CHECKING") && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 h-11"
                  onClick={() => setIsRescheduleOpen(true)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  예약변경
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 h-11 text-muted-foreground hover:bg-muted"
                  onClick={handleNoShow}
                  disabled={noShowMutation.isPending}
                >
                  <UserMinus className="h-4 w-4 mr-1" />
                  {noShowMutation.isPending ? "..." : "노쇼"}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 h-11 text-destructive border-destructive/20 hover:bg-destructive/10"
                  onClick={() => setIsCancelling(true)}
                >
                  <X className="h-4 w-4 mr-1" />
                  예약취소
                </Button>
                <Button
                  className="flex-1 h-11 bg-primary hover:bg-primary/90"
                  onClick={handleComplete}
                  disabled={completeMutation.isPending}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  {completeMutation.isPending ? "..." : "이용완료"}
                </Button>
              </div>
            )}

            {/* 최종 상태: 닫기/뒤로 */}
            {(appointment.status === "COMPLETED" ||
              appointment.status === "CANCELLED" ||
              appointment.status === "NO_SHOW") && (
              <Button variant="outline" className="w-full" onClick={onClose}>
                {isPage ? "뒤로" : "닫기"}
              </Button>
            )}
          </div>
        )}
      </footer>

      {/* 예약 변경 Dialog */}
      <RescheduleDialog
        isOpen={isRescheduleOpen}
        onClose={() => setIsRescheduleOpen(false)}
        appointment={appointment}
        onSuccess={() => {
          invalidateAll();
          onClose?.();
        }}
      />
    </div>
  );
}

// ===== Accordion Body (내부 추출) =====

interface AccordionBodyProps {
  appointment: AppointmentTimeline;
  defaultOpenSections: string[];
  hasBothChoices: boolean;
  clinicSettings: ReturnType<
    typeof import("@/hooks/useBookingDetail").useBookingDetail
  >["clinicSettings"];
  matchedProduct: ReturnType<
    typeof import("@/hooks/useBookingDetail").useBookingDetail
  >["matchedProduct"];
  staffList: ReturnType<
    typeof import("@/hooks/useBookingDetail").useBookingDetail
  >["staffList"];
  selectedStaffId: string;
  setSelectedStaffId: (id: string) => void;
  assignStaffMutation: ReturnType<
    typeof import("@/hooks/useBookingDetail").useBookingDetail
  >["assignStaffMutation"];
  statusHistory: ReturnType<
    typeof import("@/hooks/useBookingDetail").useBookingDetail
  >["statusHistory"];
  isHistoryLoading: boolean;
  appointmentHistory: ReturnType<
    typeof import("@/hooks/useBookingDetail").useBookingDetail
  >["appointmentHistory"];
  isAppointmentHistoryLoading: boolean;
  customerConsults: ReturnType<
    typeof import("@/hooks/useBookingDetail").useBookingDetail
  >["customerConsults"];
  stats: { visitCount: number; noShowCount: number; cancelCount: number };
  toggleBlockMutation: ReturnType<
    typeof import("@/hooks/useBookingDetail").useBookingDetail
  >["toggleBlockMutation"];
  handleApprove: (priority?: number) => void;
  approveMutation: ReturnType<
    typeof import("@/hooks/useBookingDetail").useBookingDetail
  >["approveMutation"];
  hasCallback: boolean;
  hasNotes: boolean;
}

function AccordionBody({
  appointment,
  defaultOpenSections,
  hasBothChoices,
  clinicSettings,
  matchedProduct,
  staffList,
  selectedStaffId,
  setSelectedStaffId,
  assignStaffMutation,
  statusHistory,
  isHistoryLoading,
  appointmentHistory,
  isAppointmentHistoryLoading,
  customerConsults,
  stats,
  toggleBlockMutation,
  handleApprove,
  approveMutation,
  hasCallback,
  hasNotes,
}: AccordionBodyProps) {
  return (
    <div className="p-4">
      <Accordion
        type="multiple"
        defaultValue={defaultOpenSections}
        className="space-y-2"
      >
        {/* 예약 정보 */}
        <AccordionItem value="info" className="border rounded-lg px-4">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              예약 정보
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              {/* V1150: 제3자(대리) 예약 배지 */}
              {appointment.isThirdParty && (
                <div className="flex items-start gap-2 rounded-md bg-violet-50 border border-violet-200 p-3">
                  <Users className="h-4 w-4 text-violet-600 mt-0.5 shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-violet-800">대리 예약</p>
                    <p className="text-violet-600 text-xs mt-0.5">
                      예약자: {appointment.bookerName || "정보 없음"}
                      {appointment.bookerPhone && ` (${appointment.bookerPhone})`}
                      {appointment.relationship && appointment.relationship !== "SELF" && (
                        <span> · 관계: {
                          ({ PARENT: "부모", CHILD: "자녀", SPOUSE: "배우자", GUARDIAN: "보호자", OTHER: "기타" } as Record<string, string>)[appointment.relationship] || appointment.relationship
                        }</span>
                      )}
                    </p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground mb-1">예약 날짜</p>
                  <p className="font-medium">
                    {appointment.firstChoiceDate
                      ? formatDateShortWithDay(appointment.firstChoiceDate)
                      : appointment.appointmentDate
                        ? formatDateShortWithDay(appointment.appointmentDate)
                        : "미정"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">예약 시간</p>
                  <p className="font-medium">
                    {appointment.firstChoiceTime || appointment.time}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">상품</p>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {appointment.serviceName || appointment.department || "-"}
                      {appointment.subItemName &&
                        ` > ${appointment.subItemName}`}
                    </span>
                    {(appointment.serviceName || appointment.department) &&
                      clinicSettings?.priceInfo &&
                      (matchedProduct ? (
                        <Badge variant="secondary" className="text-[10px] h-5">
                          등록 상품
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-[10px] h-5 border-warning/50 text-warning"
                        >
                          확인 필요
                        </Badge>
                      ))}
                  </div>
                </div>
              </div>

              {/* 1순위/2순위 */}
              {hasBothChoices && (
                <div className="text-sm border-t pt-3">
                  <p className="text-muted-foreground mb-2">
                    희망 시간 (우선순위)
                  </p>
                  <div className="space-y-2">
                    <div
                      className={cn(
                        "flex items-center gap-3 p-2 rounded-lg border",
                        appointment.isPriorityChoice === 1
                          ? "border-info/50 bg-info/10"
                          : "bg-muted/50",
                      )}
                    >
                      <div className="flex items-center justify-center h-6 w-6 rounded-full bg-info/15 text-info text-xs font-bold shrink-0">
                        1
                      </div>
                      <div className="flex-1">
                        <span className="font-medium">
                          {appointment.firstChoiceDate &&
                            formatDateShortWithDay(
                              appointment.firstChoiceDate,
                            )}{" "}
                          {appointment.firstChoiceTime}
                        </span>
                        {appointment.isPriorityChoice === 1 && " ✓"}
                      </div>
                      {appointment.needsAction && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs border-success/30 text-success hover:bg-success/10"
                          onClick={() => handleApprove(1)}
                          disabled={approveMutation.isPending}
                        >
                          <Check className="h-3 w-3 mr-1" />
                          승인
                        </Button>
                      )}
                    </div>
                    <div
                      className={cn(
                        "flex items-center gap-3 p-2 rounded-lg border",
                        appointment.isPriorityChoice === 2
                          ? "border-warning/50 bg-warning/10"
                          : "bg-muted/50",
                      )}
                    >
                      <div className="flex items-center justify-center h-6 w-6 rounded-full bg-warning/15 text-warning text-xs font-bold shrink-0">
                        2
                      </div>
                      <div className="flex-1">
                        <span className="font-medium">
                          {appointment.secondChoiceDate &&
                            formatDateShortWithDay(
                              appointment.secondChoiceDate,
                            )}{" "}
                          {appointment.secondChoiceTime || "시간 미정"}
                        </span>
                        {appointment.isPriorityChoice === 2 && " ✓"}
                      </div>
                      {appointment.needsAction && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs border-success/30 text-success hover:bg-success/10"
                          onClick={() => handleApprove(2)}
                          disabled={approveMutation.isPending}
                        >
                          <Check className="h-3 w-3 mr-1" />
                          승인
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 담당자 */}
              <div className="border-t pt-3 space-y-3">
                <div>
                  <Label className="text-sm text-muted-foreground">
                    담당 의료진
                  </Label>
                  {staffList.length > 0 ? (
                    <Select
                      value={selectedStaffId}
                      onValueChange={(value) => {
                        const staffId =
                          value === "__none__" ? null : Number(value);
                        setSelectedStaffId(value === "__none__" ? "" : value);
                        assignStaffMutation.mutate({
                          id: appointment.id,
                          staffId,
                        });
                      }}
                      disabled={assignStaffMutation.isPending}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="의료진 선택" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">미배정</SelectItem>
                        {staffList.map((staff) => (
                          <SelectItem
                            key={staff.id}
                            value={staff.id!.toString()}
                          >
                            {staff.name}
                            {staff.position && ` (${staff.position})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm mt-1">
                      {appointment.doctorName || "미배정"}
                    </p>
                  )}
                </div>
                {appointment.handlerName && (
                  <div className="text-sm">
                    <span className="text-muted-foreground">행정 담당자: </span>
                    <span>{appointment.handlerName}</span>
                  </div>
                )}
              </div>

              {/* 방문 목적 + AI 분석 */}
              {appointment.purpose && (
                <div className="border-t pt-3">
                  <p className="text-sm text-muted-foreground mb-1">
                    방문 목적
                  </p>
                  <p className="text-sm">{appointment.purpose}</p>
                  {(appointment.sentimentLabel ||
                    appointment.finalIntentLabel) && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {appointment.sentimentLabel && (
                        <Badge variant="outline" className="text-xs">
                          감정: {appointment.sentimentLabel}
                        </Badge>
                      )}
                      {appointment.finalIntentLabel && (
                        <Badge variant="outline" className="text-xs">
                          의도: {appointment.finalIntentLabel}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* 접수 경로 + 녹음 */}
        <AccordionItem value="source" className="border rounded-lg px-4">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <PhoneCall className="h-4 w-4" />
              접수 경로
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-sm flex-wrap">
                <span className="flex items-center gap-1">
                  {getSourceIcon(appointment.source)}
                  {getSourceLabel(appointment.source)}
                </span>
                {appointment.durationSeconds != null && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    {formatDuration(appointment.durationSeconds)}
                  </span>
                )}
                {appointment.callerNumber && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                    {appointment.callerNumber}
                  </span>
                )}
              </div>

              {/* AI 신뢰도 바 */}
              {appointment.aiConfidence != null && (
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">AI 신뢰도</span>
                    <span className="font-medium">
                      {Math.round(appointment.aiConfidence * 100)}%
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all",
                        appointment.aiConfidence >= 0.8
                          ? "bg-success"
                          : appointment.aiConfidence >= 0.5
                            ? "bg-warning"
                            : "bg-destructive",
                      )}
                      style={{
                        width: `${Math.round(appointment.aiConfidence * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* AI 요약 + 상담 바로가기 */}
              {appointment.purpose && (
                <div className="border-t pt-3">
                  <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    AI 요약
                  </p>
                  <p className="text-sm leading-relaxed">
                    {appointment.purpose}
                  </p>
                  {appointment.consultId && (
                    <a
                      href={`/consults/${appointment.consultId}`}
                      className="text-xs text-primary hover:underline mt-1 inline-flex items-center gap-1"
                    >
                      <FileText className="h-3 w-3" />
                      상담 상세 보기 →
                    </a>
                  )}
                </div>
              )}

              {/* 녹음 재생 (consultId가 있을 때만 — RecordingPlayer가 재시도 시 필요) */}
              {appointment.consultId && (
                <div className="border-t pt-3">
                  <RecordingPlayer
                    recordingUrl={appointment.recordingUrl}
                    consultId={appointment.consultId}
                  />
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* 고객 정보 */}
        <AccordionItem value="customer" className="border rounded-lg px-4">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              고객 정보
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              {/* 태그 */}
              {appointment.tags && appointment.tags.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">태그</p>
                  <div className="flex flex-wrap gap-1.5">
                    {appointment.tags.map((tag, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        <Tag className="h-3 w-3 mr-1" />
                        {tag.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* 키워드 */}
              {appointment.keywords && appointment.keywords.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">키워드</p>
                  <div className="flex flex-wrap gap-1.5">
                    {appointment.keywords.map((kw, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {kw.keyword}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* 예약 차단 토글 (API 연동) */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">예약 차단</p>
                      <p className="text-xs text-muted-foreground">
                        차단 시 AI 자동 예약 생성이 차단되고 콜백으로 전환
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={appointment.customerBookingBlocked ?? false}
                    onCheckedChange={(checked) => {
                      toggleBlockMutation.mutate({ blocked: checked });
                    }}
                    disabled={toggleBlockMutation.isPending}
                  />
                </div>
                {appointment.customerBlockReason && (
                  <p className="text-xs text-muted-foreground mt-1 ml-6">
                    사유: {appointment.customerBlockReason}
                  </p>
                )}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* 콜백/아웃바운드 (조건부) */}
        {hasCallback && (
          <AccordionItem value="callback" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <PhoneCall className="h-4 w-4" />
                콜백/아웃바운드
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{appointment.callbackStatus}</Badge>
                </div>
                {appointment.callbackReason && (
                  <p className="text-muted-foreground">
                    {appointment.callbackReason}
                  </p>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* 시술 안내 (조건부) */}
        {hasNotes && (
          <AccordionItem value="treatment" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                시술 안내
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3 text-sm">
                {appointment.servicePreNotes && (
                  <div>
                    <p className="font-medium text-amber-700 mb-1">
                      시술 전 주의사항
                    </p>
                    <p className="text-muted-foreground whitespace-pre-line">
                      {appointment.servicePreNotes}
                    </p>
                  </div>
                )}
                {appointment.servicePostNotes && (
                  <div>
                    <p className="font-medium text-info mb-1">
                      시술 후 주의사항
                    </p>
                    <p className="text-muted-foreground whitespace-pre-line">
                      {appointment.servicePostNotes}
                    </p>
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* 예약 메모 */}
        <AccordionItem value="memo" className="border rounded-lg px-4">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              예약 메모
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <UnifiedMemoPanel
              entityType="APPOINTMENT"
              entityId={appointment.id}
              title=""
            />
          </AccordionContent>
        </AccordionItem>

        {/* 진행이력 */}
        <AccordionItem value="progress" className="border rounded-lg px-4">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              진행이력
              {statusHistory.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {statusHistory.length}
                </Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            {isHistoryLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : statusHistory.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                이력이 없습니다
              </p>
            ) : (
              <div className="space-y-3">
                {statusHistory.map((history) => (
                  <div
                    key={history.id}
                    className="flex items-start gap-3 text-sm"
                  >
                    <div className="mt-1">
                      {getHistoryIcon(history.changeType)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">
                        {history.statusChangeDescription}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {dayjs(history.createdAt).format("M/D(ddd) HH:mm")} ·{" "}
                        {history.changedByName}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* 이용이력 */}
        <AccordionItem value="history" className="border rounded-lg px-4">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4" />
              이용이력
              {appointmentHistory && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {appointmentHistory.totalElements}
                </Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            {isAppointmentHistoryLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : !appointmentHistory?.content?.length ? (
              <p className="text-sm text-muted-foreground py-2">
                이용이력이 없습니다
              </p>
            ) : (
              <div className="space-y-2">
                {appointmentHistory.content.map((appt) => (
                  <div
                    key={appt.id}
                    className={cn(
                      "flex items-center justify-between text-sm p-2 rounded",
                      appt.id === appointment.id && "bg-muted",
                    )}
                  >
                    <div>
                      <span className="font-medium">
                        {dayjs(appt.appointmentDate).format("M/D(ddd)")}
                      </span>
                      <span className="text-muted-foreground ml-2">
                        {appt.department || "일반"}
                      </span>
                    </div>
                    <AppointmentStatusBadge status={appt.status} />
                  </div>
                ))}
                {stats.noShowCount > 0 && (
                  <div className="p-2 rounded bg-destructive/10 border border-destructive/20 text-xs text-destructive flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                    노쇼 이력이 {stats.noShowCount}건 있습니다
                  </div>
                )}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* 상담이력 */}
        <AccordionItem value="consults" className="border rounded-lg px-4">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              상담이력
              {customerConsults && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {customerConsults.totalElements}
                </Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            {!customerConsults?.content?.length ? (
              <p className="text-sm text-muted-foreground py-2">
                상담이력이 없습니다
              </p>
            ) : (
              <div className="space-y-2">
                {customerConsults.content.slice(0, 5).map((consult) => (
                  <div
                    key={consult.id}
                    className="flex items-center justify-between text-sm p-2 rounded border"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {dayjs(consult.occurredAt).format("M/D(ddd) HH:mm")}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {consult.channel === "CALL"
                            ? "AI전화"
                            : consult.channel === "MSG"
                              ? "메시지"
                              : "수기"}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {consult.summary || "요약 없음"}
                      </p>
                    </div>
                  </div>
                ))}
                {customerConsults.totalElements > 5 && (
                  <p className="text-xs text-muted-foreground text-center pt-1">
                    외 {customerConsults.totalElements - 5}건
                  </p>
                )}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
